/**
 * Client-side mirror of the server's password rules (PasswordService): at
 * least 12 characters, at most 72 bytes (bcrypt). The server stays the
 * authority; this only spares a round trip. Returns an i18n key under
 * admin:passwordReset.errors, or null when the password is acceptable.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_BYTES = 72;

const E = 'admin:passwordReset.errors';

export function newPasswordError(password: string, confirmation: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) return `${E}.tooShort`;
  if (new TextEncoder().encode(password).length > PASSWORD_MAX_BYTES) return `${E}.tooLong`;
  if (password !== confirmation) return `${E}.mismatch`;
  return null;
}

/** i18n key for an error answered by the password routes (code and HTTP status). */
export function passwordApiErrorKey(code: string | undefined, status: number | undefined): string {
  if (status === 429 || code === 'RATE_LIMITED') return `${E}.rateLimited`;
  switch (code) {
    case 'INVALID_RESET_TOKEN': return `${E}.invalidLink`;
    case 'INVALID_CURRENT_PASSWORD': return `${E}.wrongCurrent`;
    case 'WEAK_PASSWORD': return `${E}.tooShort`;
    default: return `${E}.generic`;
  }
}

export type PasswordStrength = 'tooShort' | 'weak' | 'fair' | 'strong' | 'veryStrong';

/** Bars filled out of 4 for each level of the strength meter. */
export const STRENGTH_SCORE: Record<PasswordStrength, number> = { tooShort: 1, weak: 1, fair: 2, strong: 3, veryStrong: 4 };

/**
 * Advisory strength of a new password (B3-reset-password). Below the
 * server's minimum it is "tooShort"; a repeated pattern or a single kind of
 * character stays "weak"; length (16+) and variety (3 kinds of characters
 * out of lower case, upper case, digits, symbols) raise it. The server rule
 * (12 characters) remains the only one enforced.
 */
export function passwordStrength(password: string): PasswordStrength | null {
  if (!password) return null;
  if (password.length < PASSWORD_MIN_LENGTH) return 'tooShort';
  const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(password)).length;
  const distinct = new Set(password).size;
  if (distinct < 5 || (kinds === 1 && password.length < 16)) return 'weak';
  const points = (password.length >= 16 ? 1 : 0) + (kinds >= 3 ? 1 : 0);
  return points === 2 ? 'veryStrong' : points === 1 ? 'strong' : 'fair';
}
